<script setup>
import i18n from '../../plugins/i18n'
const { t } = i18n.global
import { computed } from 'vue'
import { humanReadableTimestamp } from '@/composables/datetime'
import WYSIWYG from '@/components/WYSIWYG.vue'

const props = defineProps({ incident: { type: Object, default: () => ({}) } })

const wasCreatedAutomatically = computed(() => {
  return props.incident.triggeredBy.entityType !== 'account'
})

const showIssue = computed(() => {
  return Object.keys(props.incident.messagePayload).includes('issue')
})

const humanReadableStatus = computed(() => {
  return t(`incidents.barnIntervention.status.${props.incident.state}`)
})

const humanReadableSeverity = computed(() => {
  return t(`incidents.barnIntervention.severities.${props.incident.messagePayload.severity}`)
})

const humanReadableType = computed(() => {
  return t(`incidents.barnIntervention.types.${props.incident.messagePayload.type}`)
})

const humanReadableIssue = computed(() => {
  return t(`incidents.barnIntervention.issues.${props.incident.messagePayload.issue}`)
})

const isDescriptionEmpty = computed(() => {
  const hasDescription = Object.keys(props.incident.messagePayload).includes('description')
  return hasDescription && props.incident.messagePayload.description.length <= 7
})

const statusIcon = computed(() => {
  if (props.incident.state === 'closed') {
    return 'mdi-lock'
  }
  if (props.incident.state === 'open') {
    return 'mdi-lock-open-variant'
  }
  if (props.incident.state === 'actionRequired') {
    return 'mdi-alert'
  }
  if (props.incident.state === 'issueResolved') {
    return 'mdi-check-circle'
  }
  return 'mdi-information'
})

const statusColor = computed(() => {
  if (props.incident.state === 'closed') {
    return 'default'
  }
  if (props.incident.state === 'open') {
    return 'default'
  }
  if (props.incident.state === 'actionRequired') {
    return 'primary'
  }
  if (props.incident.state === 'issueResolved') {
    return 'success'
  }
  return 'default'
})

const severityIcon = computed(() => {
  if (props.incident.messagePayload.severity === 'none') {
    return 'mdi-checkbox-blank-circle-outline'
  }
  if (props.incident.messagePayload.severity === 'low') {
    return 'mdi-checkbox-blank-circle-outline'
  }
  if (props.incident.messagePayload.severity === 'middle') {
    return 'mdi-alert'
  }
  if (props.incident.messagePayload.severity === 'high') {
    return 'mdi-alert'
  }
  return 'mdi-checkbox-blank-circle-outline'
})

const severityColor = computed(() => {
  if (props.incident.messagePayload.severity === 'low') {
    return 'primary'
  }
  if (props.incident.messagePayload.severity === 'middle') {
    return 'warning'
  }
  if (props.incident.messagePayload.severity === 'high') {
    return 'error'
  }
  return 'primary'
})
</script>

<template>
  <v-layout-card>
    <div class="w-100 px-2 d-flex flex-row justify-space-between align-center flex-wrap">
      <div class="d-flex flex-column align-start">
        <v-card-title>
          <v-icon
            icon="mdi-calendar-check"
            start
          />
          {{ incident.messagePayload.title }}
        </v-card-title>
        <div class="mx-2 d-flex flex-row align-center ga-2 flex-wrap">
          <v-chip
            class="px-4"
            :prepend-icon="statusIcon"
            size="small"
            variant="flat"
            :color="statusColor"
          >
            {{ humanReadableStatus }}
          </v-chip>
          <v-chip
            v-if="props.incident.messagePayload.severity !== 'none'"
            class="px-4"
            :prepend-icon="severityIcon"
            size="small"
            variant="tonal"
            :color="severityColor"
          >
            {{ humanReadableSeverity }}
          </v-chip>
          <v-chip
            v-if="showIssue"
            class="px-4"
            prepend-icon="mdi-alert-circle-outline"
            size="small"
            variant="tonal"
          >
            {{ humanReadableIssue }}
          </v-chip>
        </div>
      </div>

      <v-card-subtitle>{{ humanReadableTimestamp(incident.firstEventTimestamp) }}</v-card-subtitle>

      <div class="align-self-start d-flex flex-row align-center ga-2">
        <slot name="jsonButton" />
        <slot name="commentButton" />
        <slot name="assignButton" />
      </div>
    </div>
    <v-card-text class="py-2">
      <v-list-item
        :title="$t('incidents.barnIntervention.titles.timestamp')"
        :subtitle="humanReadableTimestamp(incident.firstEventTimestamp)"
        prepend-icon="mdi-clock"
      />
      <v-list-item
        :title="$t('incidents.barnIntervention.titles.lastUpdatedTimestamp')"
        :subtitle="humanReadableTimestamp(incident.lastEventTimestamp)"
        prepend-icon="mdi-clock-end"
      />
      <v-list-item
        :title="$t('incidents.barnIntervention.titles.type')"
        :subtitle="humanReadableType"
        prepend-icon="mdi-shape"
      />
      <v-list-item
        v-if="wasCreatedAutomatically"
        :title="$t('incidents.barnIntervention.titles.created_automatically')"
        :subtitle="$t('incidents.barnIntervention.properties.created_automatically')"
        prepend-icon="mdi-robot"
      />
      <v-list-item
        v-if="!wasCreatedAutomatically"
        :title="$t('incidents.barnIntervention.titles.creator')"
        :subtitle="incident.triggeredBy.displayName"
        prepend-icon="mdi-account"
      />
      <v-list-item
        v-if="!isDescriptionEmpty"
        :title="$t('incidents.barnIntervention.titles.description')"
        prepend-icon="mdi-text-box"
      >
        <WYSIWYG
          :model-value="incident.messagePayload.description"
          :read-only="true"
          hide-details
        />
      </v-list-item>
      <v-list-item
        v-if="incident.state !== 'closed' && incident.isPublicOpen === false"
        prepend-icon="mdi-eye-off"
        :title="$t('incidents.titles.public_closed')"
        :subtitle="$t('incidents.properties.public_closed')"
      />
    </v-card-text>
    <v-card-actions class="justify-end">
      <slot
        name="genericActions"
        v-bind="{ enableNewEventButton: true, newEventButtonText: $t('incidents.barnIntervention.actions.new_event') }"
      />
    </v-card-actions>
  </v-layout-card>
</template>

<style scoped></style>
